import { strToU8, zip, type AsyncZippable } from 'fflate';
import type { BoardDoc } from '../../doc/BoardDoc';
import { RICH_TYPES, type FileItem, type ImageItem, type Item } from '../../doc/schema';
import { getAssetBlob, getAssetMeta } from '../assets/localAssets';
import { loadRich } from '../text/richLoader';

/**
 * Board backups. `board.json` is the whole document as plain JSON (rich bodies as ProseMirror
 * JSON, so it can be imported again); the ZIP adds every image and attachment next to it.
 */

export interface BackupAsset {
  id: string;
  /** Path inside the ZIP, or null when the file was not available (JSON-only export, or missing). */
  file: string | null;
  name: string;
  mime: string;
  width: number;
  height: number;
  bytes: number;
}

export interface BoardBackup {
  format: 'vds-board';
  version: 2;
  boardId: string;
  title: string;
  exportedAt: string;
  meta: Record<string, unknown>;
  layers: Record<string, unknown>;
  views: Record<string, unknown>;
  items: Item[];
  assets: BackupAsset[];
}

const EXT: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/avif': 'avif',
  'image/bmp': 'bmp',
};

function extFor(mime: string, name: string): string {
  const known = EXT[mime];
  if (known) return known;
  const fromName = /\.([a-z0-9]{1,12})$/i.exec(name)?.[1];
  return fromName ? fromName.toLowerCase() : 'bin';
}

const safeName = (s: string): string => s.replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80) || 'board';

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/** Everything in the document as plain JSON. Asset entries carry no file path until the ZIP adds one. */
export async function buildBoardBackup(board: BoardDoc, title: string): Promise<BoardBackup> {
  const all = board.index.all();
  if (all.some((i) => RICH_TYPES.has(i.type))) await loadRich();
  const items = board.snapshotItems(all.map((i) => i.id));
  const assetIds = [...new Set(items.filter((i) => i.type === 'image' || i.type === 'file').map((i) => (i as ImageItem | FileItem).props.assetId))];
  const assets: BackupAsset[] = [];
  for (const id of assetIds) {
    const m = await getAssetMeta(id);
    assets.push(
      m
        ? { id, file: null, name: m.name, mime: m.mime, width: m.width, height: m.height, bytes: m.bytes }
        : { id, file: null, name: '', mime: '', width: 0, height: 0, bytes: 0 },
    );
  }
  return {
    format: 'vds-board',
    version: 2,
    boardId: board.boardId,
    title,
    exportedAt: new Date().toISOString(),
    meta: board.meta.toJSON() as Record<string, unknown>,
    layers: board.layers.toJSON() as Record<string, unknown>,
    views: board.views.toJSON() as Record<string, unknown>,
    items,
    assets,
  };
}

export async function exportBoardJson(board: BoardDoc, title: string): Promise<void> {
  const backup = await buildBoardBackup(board, title);
  download(new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }), `${safeName(title || board.boardId)}-board.json`);
}

export interface ZipResult {
  assets: number;
  /** Asset ids whose file could not be fetched (listed with file: null in the JSON). */
  missing: string[];
}

/**
 * ZIP with board.json and assets/<id>.<ext> for every referenced image and attachment, each at
 * the size it is stored (originals are already compressed, so they are packed without deflate).
 * Everything is held in memory while packing, so this suits boards up to a few hundred MB.
 */
export async function exportBoardZip(board: BoardDoc, title: string, onProgress: (done: number, total: number) => void): Promise<ZipResult> {
  const backup = await buildBoardBackup(board, title);
  const files: AsyncZippable = {};
  const missing: string[] = [];
  let done = 0;
  onProgress(0, backup.assets.length);
  for (const a of backup.assets) {
    const blob = a.mime ? await getAssetBlob(a.id, 'original') : undefined;
    if (blob) {
      a.file = `assets/${a.id}.${extFor(a.mime, a.name)}`;
      files[a.file] = [new Uint8Array(await blob.arrayBuffer()), { level: 0 }];
    } else {
      missing.push(a.id);
    }
    onProgress(++done, backup.assets.length);
  }
  const ordered: AsyncZippable = { 'board.json': [strToU8(JSON.stringify(backup, null, 2)), { level: 6 }], ...files };
  const bytes = await new Promise<Uint8Array>((resolve, reject) => {
    zip(ordered, {}, (err, data) => (err ? reject(err) : resolve(data)));
  });
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  download(new Blob([buffer], { type: 'application/zip' }), `${safeName(title || board.boardId)}-board.zip`);
  return { assets: backup.assets.length - missing.length, missing };
}
